import axios from 'axios';
import clearSession from '../utils/clearSession';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL
});

// REQUEST INTERCEPTOR
api.interceptors.request.use((config) => {
  config.headers['B-API-Key'] = import.meta.env.VITE_API_KEY;

  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = token;
  }

  return config;
});

// REFRESH TOKEN INTERCEPTOR
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    const status = error.response?.status;

    // REFRESH IF TOKEN INVALID (401 OR 403)
    if ((status === 401 || status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // User is not logged in → just reject
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = token;
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/auth/refresh-token`,
          { refresh_token: refreshToken },
          { headers: { 'B-API-Key': import.meta.env.VITE_API_KEY } }
        );

        const newToken = response.data.data.access_token; // includes "Bearer <token>"
        const newRefresh = response.data.data.refresh_token;

        // Save new tokens
        localStorage.setItem('accessToken', newToken);
        localStorage.setItem('refreshToken', newRefresh);

        // Apply to axios defaults
        api.defaults.headers.Authorization = newToken;

        processQueue(null, newToken);

        // Apply token to original failed request
        originalRequest.headers.Authorization = newToken;

        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        clearSession();
        window.location.href = '/login';
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
